/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Loader from 'components/atoms/loader';
import AiManagementAnalystStatus from 'components/molecules/ai_management_analyst_status';
import AiManagementProposalCheck from 'components/molecules/ai_management_proposal_check';
import AiManagementProposalFinal from 'components/molecules/ai_management_proposal_final';
import AiManagementEditalPanel, {
  EditalPanelTarget,
} from 'components/organisms/ai_management_edital_panel';
import AiManagementProposalItems from 'components/molecules/ai_management_proposal_items';
import AiManagementProposalCompanyStep from 'components/molecules/ai_management_proposal_company_step';
import AiManagementProposalDocumentsStep from 'components/molecules/ai_management_proposal_documents_step';
import AiManagementProposalGenerate from 'components/molecules/ai_management_proposal_generate';
import AiManagementProposalRules from 'components/molecules/ai_management_proposal_rules';
import AiManagementProposalStepper from 'components/molecules/ai_management_proposal_stepper';
import { errorMessageOf } from 'hooks/useDocumentRequirements';
import { useProposal } from 'hooks/useProposal';
import { getProposal } from 'services/actions/ai_management_proposal';
import {
  ProposalCheckResponse,
  reviewProposal,
} from 'services/actions/ai_management_proposal_check';
import {
  PROPOSAL_ANALYSIS_ERROR,
  PROPOSAL_STATUS_LABEL,
  PROPOSAL_STEPS,
  ProposalStep,
  proposalStepLabel,
  RULES_LOCK_MESSAGE,
} from 'services/ai_management_proposal';
import { BiddingData } from 'store/modules/bidding_store/types';
import { ProposalShell, ProposalShellContext } from './context';
import { Container } from './styles';

interface AiManagementBiddingProposalProps {
  bidding: BiddingData | null;
  loading: boolean;
  error: boolean;
  onBidderFilesChanged?: () => void;
}

const PROCESSING_MESSAGE = (
  <>
    <strong>Analisando…</strong> Procurando o modelo de proposta e as regras no edital.
  </>
);

const RESUMABLE_STATUSES = ['rascunho', 'revisada'];

const STEP_SUBTITLE: Record<ProposalStep, string> = {
  regras:
    'O Analista encontrou estas regras no edital. Confirme as que valem para a sua proposta e marque as que não se aplicam ao que você vende. Só as regras válidas entram na checagem.',
  itens:
    'Selecione os itens que você vai disputar e preencha o valor unitário. Se preferir, use a planilha.',
  empresa: 'Confira os dados da empresa e preencha as condições da proposta.',
  declaracoes:
    'Declarações e documentos que o edital pede para a proposta. Os documentos de habilitação ficam em Preparar Habilitação.',
  revisao: 'Confira se a proposta atende às regras antes de gerar.',
};

const AiManagementBiddingProposal: React.FC<AiManagementBiddingProposalProps> = ({
  bidding,
  loading,
  error,
  onBidderFilesChanged,
}) => {
  const [analyzeError, setAnalyzeError] = useState('');
  // "Seguir sem o modelo do edital" só fica gravado quando o cliente confirma as regras.
  const [withoutModel, setWithoutModel] = useState(false);
  const [activeStep, setActiveStep] = useState<ProposalStep>('regras');
  // Etapa em que o cliente parou, lida uma vez ao abrir a seção; some quando ele navega.
  const [resumeStep, setResumeStep] = useState<ProposalStep | null>(null);
  const [resumeChecked, setResumeChecked] = useState(false);
  // Contagem que a etapa aberta informa enquanto o cliente edita; vale sobre a do servidor.
  const [pendencies, setPendenciesState] = useState<Partial<Record<ProposalStep, number>>>({});
  const [saving, setSaving] = useState(0);
  const [saved, setSaved] = useState(false);
  const [check, setCheck] = useState<ProposalCheckResponse | null>(null);
  const [focusTarget, setFocusTarget] = useState<string | null>(null);
  // "Editar proposta" na tela final volta à revisão sem mudar o status "Gerada".
  const [editingGenerated, setEditingGenerated] = useState(false);
  const [stepActions, setStepActions] = useState<HTMLElement | null>(null);
  const [footerActions, setFooterActions] = useState<HTMLElement | null>(null);
  const [editalAberto, setEditalAberto] = useState<EditalPanelTarget | null>(null);

  const biddingId = bidding ? Number(bidding.bidding_id) || Number(bidding.id) : null;
  const { proposal, analyzing, saveStep, applyProposal, ...state } = useProposal(biddingId);
  const analysis = proposal?.analysis ?? null;
  const statusRef = useRef(proposal?.status);
  statusRef.current = proposal?.status;

  useEffect(() => {
    setAnalyzeError('');
    setWithoutModel(false);
    setActiveStep('regras');
    setResumeStep(null);
    setResumeChecked(false);
    setPendenciesState({});
    setSaving(0);
    setSaved(false);
    setCheck(null);
    setFocusTarget(null);
    setEditingGenerated(false);
    setEditalAberto(null);
  }, [biddingId]);

  // Abrir a revisão marca a proposta em rascunho como "Revisada"; gerada não muda.
  useEffect(() => {
    if (activeStep !== 'revisao' || !biddingId || statusRef.current !== 'rascunho') return;
    const id = biddingId;
    reviewProposal(id)
      .then((res) => {
        if (res?.status && res.status < 400 && res.data) applyProposal(id, res.data);
      })
      .catch(() => undefined);
  }, [activeStep, biddingId, applyProposal]);

  useEffect(() => {
    if (!proposal || resumeChecked) return;
    setResumeChecked(true);
    if (proposal.status === 'gerada') {
      setActiveStep('revisao');
      return;
    }
    if (
      RESUMABLE_STATUSES.includes(proposal.status) &&
      proposal.rulesConfirmed &&
      proposal.step !== 'regras'
    ) {
      setResumeStep(proposal.step);
    }
  }, [proposal, resumeChecked]);

  const handleAnalyze = () => {
    setAnalyzeError('');
    setWithoutModel(false);
    state
      .analyze()
      .catch((err) =>
        setAnalyzeError(errorMessageOf(err, 'Não foi possível iniciar a análise da proposta.')),
      );
  };

  const rulesConfirmed = !!proposal?.rulesConfirmed;
  // Refs para o contexto manter a mesma identidade: etapas usam as funções em efeitos.
  const rulesConfirmedRef = useRef(rulesConfirmed);
  rulesConfirmedRef.current = rulesConfirmed;
  const biddingIdRef = useRef(biddingId);
  biddingIdRef.current = biddingId;

  const goToStep = useCallback(
    (step: ProposalStep, target?: string) => {
      if (step !== 'regras' && !rulesConfirmedRef.current) return;
      setResumeStep(null);
      setActiveStep(step);
      setFocusTarget(target ?? null);
      saveStep(step);
    },
    [saveStep],
  );

  // A etapa carrega depois de aberta: procura o alvo por alguns segundos, rola até ele e o destaca.
  useEffect(() => {
    if (!focusTarget) return undefined;
    const selector = `[data-proposal-target="${CSS.escape(focusTarget)}"]`;
    let tentativas = 0;
    const interval = setInterval(() => {
      tentativas += 1;
      const el = document.querySelector(selector);
      if (!el && tentativas < 40) return;
      clearInterval(interval);
      setFocusTarget(null);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('proposal-target-highlight');
      // Fora da limpeza do efeito: achar o alvo zera o focusTarget, o que dispara a limpeza.
      setTimeout(() => el.classList.remove('proposal-target-highlight'), 3000);
    }, 200);
    return () => clearInterval(interval);
  }, [focusTarget, activeStep]);

  const activeIndex = PROPOSAL_STEPS.indexOf(activeStep);
  const canGoPrev = activeIndex > 0;
  const canGoNext = activeIndex >= 0 && activeIndex < PROPOSAL_STEPS.length - 1;
  const goPrev = () => {
    if (canGoPrev) goToStep(PROPOSAL_STEPS[activeIndex - 1]);
  };
  const goNext = () => {
    if (canGoNext) goToStep(PROPOSAL_STEPS[activeIndex + 1]);
  };

  const shell = useMemo<ProposalShell>(
    () => ({
      setPendencies: (step, count) =>
        setPendenciesState((prev) => (prev[step] === count ? prev : { ...prev, [step]: count })),
      // Gravação iniciada em outra licitação não mexe no indicador da atual.
      trackSave: (request) => {
        const id = biddingIdRef.current;
        const current = () => biddingIdRef.current === id;
        setSaving((value) => value + 1);
        return request.then(
          (result) => {
            if (current()) {
              setSaving((value) => Math.max(0, value - 1));
              setSaved(true);
              // As gravações de itens, condições, empresa e declarações não devolvem a proposta; o
              // back volta revisada/gerada para rascunho, então a casca relê o status.
              if (id && ['revisada', 'gerada'].includes(statusRef.current ?? '')) {
                getProposal(id)
                  .then((res) => {
                    if (current() && res?.data) applyProposal(id, res.data);
                  })
                  .catch(() => undefined);
              }
            }
            return result;
          },
          (err) => {
            if (current()) {
              setSaving((value) => Math.max(0, value - 1));
              setSaved(false);
            }
            throw err;
          },
        );
      },
      goToStep,
      applyProposal: (data) => {
        if (!biddingIdRef.current) return;
        applyProposal(biddingIdRef.current, data);
        // Quem grava as regras e avança na mesma ação não pode esperar o próximo render.
        if (typeof data?.rules_confirmed === 'boolean') {
          rulesConfirmedRef.current = data.rules_confirmed;
        }
      },
      focusTarget,
      stepActions,
      footerActions,
    }),
    [goToStep, applyProposal, focusTarget, stepActions, footerActions],
  );

  const analysisFinished = analysis?.status === 'done' || analysis?.status === 'partial';
  // Natureza só é confiável com a análise terminada (done/partial); servico/obra param no aviso
  // de bloqueio e nunca chegam às etapas.
  const blockedByNatureza =
    analysisFinished && (analysis?.natureza === 'servico' || analysis?.natureza === 'obra');
  const showSteps =
    !blockedByNatureza && !!proposal && (analysisFinished || withoutModel || rulesConfirmed);
  // Com qualquer validação gravada, parte das regras gravadas; antes do primeiro PATCH, das regras
  // da análise, todas "A validar".
  // A análise é a fonte da controversa: regra gravada antes de virar controversa (reanálise) ganha
  // as leituras e, sem leitura escolhida, volta a "A validar" (o back não aceita "válida" sem ela).
  const analysisRules = analysis?.rules ?? [];
  const savedRules = (proposal?.rules ?? []).map((rule) => {
    const origem = analysisRules.find((r) => r.regra === rule.regra);
    if (rule.origem === 'cliente' || rule.controversa || origem?.controversa !== true) return rule;
    const semLeitura = typeof rule.leitura_escolhida !== 'number';
    return {
      ...rule,
      controversa: true,
      motivo: origem.motivo ?? null,
      leituras: origem.leituras ?? [],
      status: semLeitura && rule.status === 'valida' ? ('a_validar' as const) : rule.status,
    };
  });
  const allRules = savedRules.length > 0 ? savedRules : analysisRules;
  // "Seguir sem o modelo" descarta as regras do edital, mas nunca as que o cliente adicionou.
  const displayedRules = allRules.filter((rule) => rule.origem === 'cliente' || !withoutModel);
  const editalHasRules = displayedRules.some((rule) => rule.origem !== 'cliente');
  // A tela final ocupa a seção inteira: sem stepper, sem título da etapa e sem rodapé.
  const telaFinal =
    showSteps &&
    activeStep === 'revisao' &&
    !!proposal &&
    proposal.status === 'gerada' &&
    proposal.generatedFiles.length > 0 &&
    !editingGenerated;

  const renderIntro = () => (
    <div className="proposal-empty card text-center px-4 py-3 mx-auto mt-5">
      <p className="proposal-empty-title mb-1">
        Você ainda não elaborou a proposta para essa licitação.
      </p>
      <p className="mb-0">
        Use nosso{' '}
        <button
          type="button"
          className="btn btn-link analyst-link p-0 align-baseline"
          onClick={handleAnalyze}
          disabled={analyzing}
        >
          <i className="material-icons-outlined mr-1">smart_toy</i>
          Analista de Propostas
        </button>{' '}
        para buscar no edital o modelo de proposta e as regras.
      </p>
      {analyzeError && <div className="alert alert-danger mt-3 mb-0">{analyzeError}</div>}
    </div>
  );

  // Natureza servico/obra não é suportada nesta versão: só o aviso, sem "Analisar de novo" nem
  // "Seguir sem o modelo do edital".
  const renderBlockedNotice = () => (
    <div className="proposal-empty card text-center px-4 py-3 mx-auto mt-5">
      <p className="proposal-goods-notice mb-0">
        Nesta versão, o Elaborar Proposta atende licitações de bens.
      </p>
      <p className="proposal-goods-notice text-muted small mb-0">
        O suporte a esse tipo de licitação chega em novembro.
      </p>
    </div>
  );

  // Prévia do edital no painel lateral; a regra leva o arquivo e a página do trecho, quando há.
  const abrirEdital = (alvo?: { arquivo?: string | null; pagina?: number | null }) =>
    setEditalAberto({ arquivo: alvo?.arquivo ?? null, pagina: alvo?.pagina ?? null });

  const renderAnalysisStatus = () => (
    <AiManagementAnalystStatus
      analysis={
        analysis && { status: analysis.status, warnings: [], errorMessage: PROPOSAL_ANALYSIS_ERROR }
      }
      reanalyzing={analyzing}
      reanalyzeError={analyzeError}
      onReanalyze={handleAnalyze}
      processingMessage={PROCESSING_MESSAGE}
      errorHint=""
      errorActions={
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary ml-2"
          onClick={() => setWithoutModel(true)}
          disabled={analyzing}
        >
          Seguir sem o modelo do edital
        </button>
      }
    />
  );

  // A checagem não confere tudo que o stepper conta (ex.: item sem preço num edital com regras):
  // "Gerar proposta" bloqueia pelas duas.
  const pendenciasParaGerar = PROPOSAL_STEPS.filter((step) => step !== 'revisao').reduce(
    (soma, step) => soma + (({ ...proposal?.pendencies, ...pendencies }[step] as number) || 0),
    0,
  );

  const renderGenerate = () => (
    <AiManagementProposalGenerate
      biddingId={biddingId as number}
      bidding={bidding ?? {}}
      proposal={proposal}
      pendencias={pendenciasParaGerar}
      checagem={
        check && { podeGerar: check.pode_gerar, naoAtendidas: check.contadores.nao_atendidas }
      }
      onRegistered={(data) => {
        setEditingGenerated(false);
        applyProposal(biddingId as number, data);
      }}
      onFilesUploaded={onBidderFilesChanged}
    />
  );

  const renderStepContent = () => {
    if (activeStep === 'itens') {
      return <AiManagementProposalItems biddingId={biddingId as number} />;
    }

    if (activeStep === 'empresa') {
      return <AiManagementProposalCompanyStep biddingId={biddingId as number} />;
    }

    if (activeStep === 'declaracoes') {
      return (
        <AiManagementProposalDocumentsStep
          biddingId={biddingId as number}
          editalNumber={bidding?.edital ?? null}
          bidding={bidding}
          onOpenEdital={abrirEdital}
        />
      );
    }

    if (activeStep === 'revisao') {
      if (telaFinal && proposal) {
        return (
          <AiManagementProposalFinal
            biddingId={biddingId as number}
            proposal={proposal}
            editalNumber={bidding?.edital ?? null}
            onEdit={() => setEditingGenerated(true)}
          />
        );
      }
      const etapasPendentes = PROPOSAL_STEPS.filter((step) => step !== 'revisao')
        .map((step, index) => ({
          step,
          numero: index + 1,
          count: ({ ...proposal?.pendencies, ...pendencies }[step] as number) || 0,
        }))
        .filter((etapa) => etapa.count > 0);
      return (
        <>
          {etapasPendentes.length > 0 && (
            <section className="missing-card">
              <h3>
                <i className="material-icons-outlined" aria-hidden="true">
                  error_outline
                </i>
                O que falta para gerar a proposta
              </h3>
              <ul>
                {etapasPendentes.map((etapa) => (
                  <li key={etapa.step}>
                    <span>
                      <strong>
                        {etapa.numero}. {proposalStepLabel(etapa.step, editalHasRules)}
                      </strong>{' '}
                      · {etapa.count} {etapa.count === 1 ? 'pendência' : 'pendências'}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => goToStep(etapa.step)}
                    >
                      Ir para a etapa
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <AiManagementProposalCheck
            biddingId={biddingId as number}
            rules={displayedRules}
            onOpenEdital={abrirEdital}
            onCheckChange={setCheck}
            saving={saving}
          />
          {renderGenerate()}
        </>
      );
    }

    if (activeStep !== 'regras') {
      return <p className="text-muted mb-0">{proposalStepLabel(activeStep, editalHasRules)}</p>;
    }

    return (
      <>
        <AiManagementProposalRules
          biddingId={biddingId as number}
          rules={displayedRules}
          partial={analysis?.status === 'partial'}
          modelMissing={analysis?.modelFound === false}
          onOpenEdital={abrirEdital}
          onContinue={() => goToStep('itens')}
        />
        {withoutModel && (
          <button
            type="button"
            className="btn btn-link p-0 mt-2"
            onClick={handleAnalyze}
            disabled={analyzing}
          >
            Analisar de novo
          </button>
        )}
        {analyzeError && <div className="alert alert-danger mt-3 mb-0">{analyzeError}</div>}
      </>
    );
  };

  const renderSteps = () => (
    <ProposalShellContext.Provider value={shell}>
      {resumeStep && rulesConfirmed && (
        <div className="alert alert-info d-flex align-items-center justify-content-between mb-3">
          <span>
            <strong>Proposta não finalizada</strong> · você parou em{' '}
            {proposalStepLabel(resumeStep, editalHasRules)}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-primary ml-3 flex-shrink-0"
            onClick={() => goToStep(resumeStep)}
          >
            Continuar de onde parei
          </button>
        </div>
      )}

      {!telaFinal && (
        <>
          <AiManagementProposalStepper
            activeStep={activeStep}
            locked={!rulesConfirmed}
            editalHasRules={editalHasRules}
            pendencies={{ ...proposal?.pendencies, ...pendencies }}
            resumeStep={resumeStep}
            onSelect={goToStep}
          />

          <p className="edict-check">
            <i className="material-icons-outlined" aria-hidden="true">
              fact_check
            </i>
            O Analista IA pode errar. Confira as informações no edital antes de seguir.
            {biddingId && (
              <button type="button" onClick={() => abrirEdital()}>
                Abrir edital
                <i className="material-icons-outlined" aria-hidden="true">
                  open_in_new
                </i>
              </button>
            )}
          </p>

          {analysis?.status === 'processing' && renderAnalysisStatus()}

          <div className="step-toolbar">
            <div>
              <h2>{proposalStepLabel(activeStep, editalHasRules)}</h2>
              <small>
                {activeStep === 'regras' && displayedRules.length === 0
                  ? 'Regras que você adicionou. Elas entram na checagem antes de gerar a proposta.'
                  : STEP_SUBTITLE[activeStep]}
              </small>
            </div>
            <div className="step-toolbar-actions" ref={setStepActions} />
          </div>
        </>
      )}
      {renderStepContent()}
    </ProposalShellContext.Provider>
  );

  const renderContent = () => {
    if (loading && !bidding) return <Loader />;
    if (error || state.error) {
      return (
        <div className="alert alert-warning">
          Não foi possível carregar a proposta desta licitação.
        </div>
      );
    }
    if (state.loading) return <Loader />;
    if (!proposal) return null;
    if (blockedByNatureza) return renderBlockedNotice();
    if (showSteps) return renderSteps();
    if (analysis?.status === 'processing' || analysis?.status === 'error') {
      return renderAnalysisStatus();
    }
    return renderIntro();
  };

  const renderSaveState = () => {
    if (saving > 0) return 'Salvando…';
    if (saved) return 'Salvo há instantes';
    return null;
  };

  return (
    <Container className="d-flex">
      <div className="proposal-main d-flex flex-column flex-grow-1 position-relative">
        <div className="proposal-scroll overflow-auto px-4 pt-4 pb-3">
          <div className="page-head">
            <div className="d-flex align-items-center">
              <h1>Preparar proposta</h1>
              {proposal && !telaFinal && (
                <span className="badge badge-light proposal-status ml-3">
                  {PROPOSAL_STATUS_LABEL[proposal.status]}
                </span>
              )}
            </div>
            {showSteps && !telaFinal && (
              <p className="subtitle">
                {analysis?.modelFound === false
                  ? 'Não encontramos um modelo de proposta no edital. A proposta vai usar um layout simples montado com as exigências do edital.'
                  : 'Modelo de proposta extraído do edital.'}
              </p>
            )}
          </div>

          {renderContent()}
        </div>

        {showSteps && !telaFinal && (
          <div className="proposal-footer-bar d-flex align-items-center justify-content-between">
            <span className="autosave" aria-live="polite">
              {saving > 0 && <span className="autosave-spinner" aria-hidden="true" />}
              {saving === 0 && saved && (
                <i className="material-icons-outlined" aria-hidden="true">
                  cloud_done
                </i>
              )}
              {renderSaveState()}
            </span>
            <div className="footer-actions">
              {!rulesConfirmed && activeStep !== 'regras' && <small>{RULES_LOCK_MESSAGE}.</small>}
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={goPrev}
                disabled={!canGoPrev}
              >
                Voltar
              </button>
              <div className="footer-step-actions" ref={setFooterActions} />
              {activeStep !== 'revisao' && activeStep !== 'regras' && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={goNext}
                  disabled={!canGoNext || !rulesConfirmed}
                  title={!rulesConfirmed ? RULES_LOCK_MESSAGE : undefined}
                >
                  Continuar
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      {editalAberto && biddingId && (
        <AiManagementEditalPanel
          biddingId={biddingId}
          title={`Edital — ${
            [bidding?.modality?.nome, bidding?.edital].filter(Boolean).join(' ') || 'licitação'
          }`}
          target={editalAberto}
          onClose={() => setEditalAberto(null)}
        />
      )}
    </Container>
  );
};

export default AiManagementBiddingProposal;
